import { fetch } from 'owa-fetch';
import { DatapointStatus } from 'owa-analytics';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { getAccessTokenforResource } from 'owa-tokenprovider';
import { getApplicationSettings } from 'owa-application-settings';

import type { PerformanceDatapoint } from 'owa-analytics';

const getGraphEndpoint = () => {
    const { resourceUrl } = getApplicationSettings('Graph');
    return resourceUrl;
};

export const getGraphEndpointValidateProperties = (): string => {
    return getGraphEndpoint() + '/v1.0/directoryObjects/validateProperties';
};

/**
 * Get the token to call MS graph.
 * @returns OAuth token required to call MS Graph
 */
export async function getToken(): Promise<string | undefined> {
    return getAccessTokenforResource(
        getGraphEndpoint() /** resource endpoint */,
        'directoryObjects' /** API name */,
        undefined /* requestId */,
        undefined /* targetTenantId */,
        undefined /* wwwAuthenticateHeader */,
        undefined /* preferIdpToken */,
        getModuleContextMailboxInfo() /** mailboxInfo */
    );
}

/** Reusable method to call to MS Graph
 * @param endPoint: MS Graph endpoint, ex: https://graph.microsoft.com/v1.0/directoryObjects/validateProperties
 * @param apiQuery: the api query, ex: /{id}/memberOf
 * @param userId: the user id or group Id, ex: oid@tid, or SMTP (UPN)
 * @param accessToken: the access token, if not provided, will be retrieved
 * @param method: the http method, ex: GET, POST, DELETE
 * @param jsonBody: the json body to send in the request
 */
export async function callMsGraphValidateProperties(
    endPoint: string,
    accessToken?: string,
    method?: string,
    jsonBody?: string
): Promise<Response> {
    // Get the token to call MS graph.
    accessToken = accessToken ?? ((await getToken()) as string);
    try {
        return callMsGraphInternal(endPoint, accessToken, method, jsonBody);
    } catch (e) {
        throw e;
    }
}

/** Internal method call to MS Graph
 * @param url: MS Graph endpoint, ex: https://graph.microsoft.com/v1.0/directoryObjects/validateProperties
 * @param accessToken: the access token, if not provided, will be retrieved
 * @param method: the http method, ex: GET, POST, DELETE
 * @param jsonBody: the json body to send in the request
 * @param dp: the performance datapoint to track the call
 */
async function callMsGraphInternal(
    url: string,
    accessToken: string,
    method?: string,
    jsonBody?: string,
    dp?: PerformanceDatapoint
) {
    try {
        const options = {
            method: method ?? 'GET',
            headers: {
                Authorization: accessToken,
                'Content-Type': 'application/json' /** Required for POST */,
            },
            body: jsonBody,
        };
        // call MS Graph API.
        const response = await fetch(url, options);
        dp?.end();
        return response;
    } catch (e) {
        dp?.endWithError(DatapointStatus.ServerError);
        throw e;
    }
}
